/**
 * Dual AI Access Model
 * - Match AI (Tier 1): bound to active match context. Available to all plans
 *   under existing daily token limits.
 * - Global Soccer Oracle (Tier 2): premium. Free users get a small daily
 *   trial; Pro/Enterprise/Sponsored Premium (per flag) get full access.
 *
 * This module is pure logic + tiny localStorage trial counter so it can be
 * consumed from server route, UI gating, and admin panel alike.
 */
import { supabase } from "@/integrations/supabase/client";
import type { PlanCode } from "@/lib/billing/resolvePlan";

export type AiChannel = "match_ai" | "global_oracle";
export type AiIntent = "match_context" | "global_soccer" | "out_of_scope";

export interface GlobalOracleSettings {
  enabled: boolean;
  trial_questions_per_day: number; // free users
  max_words_free: number;
  max_words_pro: number;
  max_words_sponsored_premium: number;
  max_words_enterprise: number;
  pro_questions_per_day: number;
  sponsored_premium_questions_per_day: number;
  enterprise_questions_per_day: number; // 0 = unlimited / customizable
  sponsored_premium_includes_global: boolean;
}

export const DEFAULT_GO_SETTINGS: GlobalOracleSettings = {
  enabled: true,
  trial_questions_per_day: 10,
  max_words_free: 120,
  max_words_pro: 300,
  max_words_sponsored_premium: 300,
  max_words_enterprise: 800,
  pro_questions_per_day: 200,
  sponsored_premium_questions_per_day: 200,
  enterprise_questions_per_day: 0,
  sponsored_premium_includes_global: true,
};

const SETTINGS_KEY = "global_oracle_settings";
let cached: GlobalOracleSettings | null = null;
let cachedAt = 0;

export async function loadGlobalOracleSettings(force = false): Promise<GlobalOracleSettings> {
  if (!force && cached && Date.now() - cachedAt < 60_000) return cached;
  try {
    // P0-AUTH-08 — explicit 4s timeout. On Android Chrome `navigator.locks`
    // contention can stall PostgREST requests indefinitely while the auth
    // bootstrap holds the auth-token lock. Fall back to defaults instead of
    // hanging the Oracle UI in an infinite spinner.
    const query = supabase
      .from("system_config")
      .select("value")
      .eq("key", SETTINGS_KEY)
      .maybeSingle();
    const { data } = await Promise.race([
      query,
      new Promise<{ data: null }>((resolve) =>
        setTimeout(() => resolve({ data: null }), 4000),
      ),
    ]);
    cached = { ...DEFAULT_GO_SETTINGS, ...((data?.value as Partial<GlobalOracleSettings>) ?? {}) };
  } catch {
    cached = { ...DEFAULT_GO_SETTINGS };
  }
  cachedAt = Date.now();
  return cached;
}

export async function saveGlobalOracleSettings(s: GlobalOracleSettings): Promise<void> {
  await supabase
    .from("system_config")
    .upsert(
      { key: SETTINGS_KEY, value: JSON.parse(JSON.stringify(s)), updated_at: new Date().toISOString() },
      { onConflict: "key" },
    );
  cached = s;
  cachedAt = Date.now();
}

/** Lightweight intent classifier (cheap, deterministic). */
export function classifyAiIntent(question: string): AiIntent {
  const q = (question ?? "").toLowerCase();
  if (!q.trim()) return "out_of_scope";
  // Match-context cues: current match keywords or short follow-ups.
  if (
    /\b(este partido|this match|deste jogo|partido actual|current match|score|marcador|minuto|minute|minuto atual|alineaci[oó]n|lineup|sustituci[oó]n|sub|cambio|var|tarjeta|card|penal|ataque|presi[oó]n|momentum|h2h|cara a cara|hoy|today|hoje|en vivo|live|ao vivo|ahora|now|agora)\b/.test(q)
  ) {
    return "match_context";
  }
  // Global soccer cues
  if (
    /\b(world cup|mundial|copa do mundo|champions|premier league|la liga|laliga|serie a|bundesliga|liga mx|libertadores|hist[oó]ric|hist[oó]ria|hist[oó]rico|r[eé]cord|record|ranking|transfer|fichaje|legend|leyenda|maradona|pel[eé]|pele|messi (vs|versus)|cristiano|ronaldo|trofeo|trophy|bal[oó]n de oro|ballon)\b/.test(q)
  ) {
    return "global_soccer";
  }
  if (/\b(f[uú]tbol|football|soccer|futebol|jugador|player|jogador|equipo|team|liga|league|t[aá]ctica|tactic|estad[ií]stica|stats)\b/.test(q)) {
    return "global_soccer";
  }
  return "out_of_scope";
}

export interface OracleAccess {
  allowed: boolean;
  reason: "premium" | "trial_available" | "trial_exhausted" | "disabled";
  trial_remaining: number;
  is_premium: boolean;
  max_words: number;
}

function trialKey(scope = "free"): string {
  const d = new Date();
  const ymd = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
  return `ss2026:gso:trial:${scope}:${ymd}`;
}

export function getTrialUsed(scope = "free"): number {
  if (typeof window === "undefined") return 0;
  return Number(window.localStorage.getItem(trialKey(scope)) ?? "0") || 0;
}

export function bumpTrialUsed(scope = "free"): number {
  if (typeof window === "undefined") return 0;
  const next = getTrialUsed(scope) + 1;
  window.localStorage.setItem(trialKey(scope), String(next));
  return next;
}

export function maxWordsForPlan(plan: PlanCode, s: GlobalOracleSettings): number {
  if (plan === "enterprise") return s.max_words_enterprise;
  if (plan === "pro") return s.max_words_pro;
  if (plan === "sponsored_premium") return s.max_words_sponsored_premium;
  return s.max_words_free;
}

export function evaluateOracleAccess(plan: PlanCode, s: GlobalOracleSettings): OracleAccess {
  const max_words = maxWordsForPlan(plan, s);
  if (!s.enabled) {
    return { allowed: false, reason: "disabled", trial_remaining: 0, is_premium: false, max_words };
  }
  const sponsoredPremium = plan === "sponsored_premium" && s.sponsored_premium_includes_global;
  const isPremium = plan === "pro" || plan === "enterprise" || sponsoredPremium;
  if (isPremium) {
    const cap =
      plan === "enterprise"
        ? s.enterprise_questions_per_day
        : plan === "pro"
          ? s.pro_questions_per_day
          : s.sponsored_premium_questions_per_day;
    if (!cap || cap <= 0) {
      return { allowed: true, reason: "premium", trial_remaining: -1, is_premium: true, max_words };
    }
    const used = getTrialUsed(plan);
    const remaining = Math.max(0, cap - used);
    return {
      allowed: remaining > 0,
      reason: remaining > 0 ? "premium" : "trial_exhausted",
      trial_remaining: remaining,
      is_premium: true,
      max_words,
    };
  }
  const used = getTrialUsed();
  const remaining = Math.max(0, s.trial_questions_per_day - used);
  return {
    allowed: remaining > 0,
    reason: remaining > 0 ? "trial_available" : "trial_exhausted",
    trial_remaining: remaining,
    is_premium: false,
    max_words,
  };
}